import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { toast } from "@/utils/appToast";
import ContentContainer from '@/components/containers/contentContainer';
import Popup from '@/shared/popups/popupDetails';
import { api } from '@/utils/api';
import { getBackendCallErrorMessage } from '@/utils/backendErrorMessage';
import JobSkeleton from '@/utils/jobskeleton';
import NewProposalForm from '@/pages/hire/new-proposal-form';

type StatusFilter = 'all' | 'submitted' | 'accepted' | 'rejected';

const unwrapProposals = (res: any): any[] => {
  if (!res) return [];
  const body = res.success !== undefined ? res.data : res;
  if (!body) return [];
  if (Array.isArray(body.proposals)) return body.proposals;
  if (Array.isArray(body.data?.proposals)) return body.data.proposals;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body)) return body;
  return [];
};

const getClientName = (proposal: any) => {
  const client = proposal?.clientId;
  if (!client || typeof client !== 'object') return '-';
  const name = [client.first_name, client.last_name].filter(Boolean).join(' ').trim();
  return name || client.email || '-';
};

const getServiceName = (proposal: any, lang?: string) => {
  const service = proposal?.requestQuoteJob?.service;
  if (!service) return '-';
  if (typeof service === 'string') return service;
  return (
    service?.mlname?.[lang || 'en'] ||
    service?.mlname?.en ||
    service?.name ||
    '-'
  );
};

const statusBadgeClass = (status: string) => {
  if (status === 'submitted')
    return 'bg-[#e7eeff] text-[#4285F4] border-[#d8e3fb]';
  if (status === 'accepted')
    return 'bg-green-50 text-green-700 border-green-200';
  if (status === 'rejected') return 'bg-red-50 text-red-700 border-red-200';
  return 'bg-[#f9f9ff] text-[#424654] border-[#d8e3fb]';
};

const ProviderProposalsPage = () => {
  const { t, i18n } = useTranslation();
  const [proposals, setProposals] = useState<any[]>([]);
  const [fetching, setFetching] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [activeRequestQuoteJobId, setActiveRequestQuoteJobId] = useState<string | null>(
    null
  );
  const [showFormPopup, setShowFormPopup] = useState(false);

  const fetchProposals = async () => {
    setFetching(true);
    try {
      const res = await api.getMyProposals();
      if (res?.success === false) {
        toast.error(
          getBackendCallErrorMessage(res) ||
            res?.message ||
            'Failed to load proposals'
        );
        setProposals([]);
        return;
      }
      setProposals(unwrapProposals(res));
    } catch (err: any) {
      toast.error(err?.message || 'Failed to load proposals');
      setProposals([]);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchProposals();
  }, []);

  const filteredProposals = useMemo(() => {
    if (statusFilter === 'all') return proposals;
    return proposals.filter((p) => p?.status === statusFilter);
  }, [proposals, statusFilter]);

  const openProposalForm = (proposal: any) => {
    const jobId =
      proposal?.requestQuoteJobId ||
      proposal?.requestQuoteJob?._id ||
      null;
    if (!jobId) {
      toast.error('Request quote job id missing for this proposal.');
      return;
    }
    setActiveRequestQuoteJobId(String(jobId));
    setShowFormPopup(true);
  };

  const closeFormPopup = () => {
    setShowFormPopup(false);
    setActiveRequestQuoteJobId(null);
  };

  const filters: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: t('all') || 'All' },
    { key: 'submitted', label: t('proposalStatus_submitted') || 'Submitted' },
    { key: 'accepted', label: t('proposalStatus_accepted') || 'Accepted' },
    { key: 'rejected', label: t('proposalStatus_rejected') || 'Rejected' },
  ];

  return (
    <ContentContainer className="!px-0 m-0 bg-[#f9f9ff] min-h-screen">
      <div className="px-4 sm:px-6 pt-6 pb-2">
        <h1 className="text-xl sm:text-2xl font-bold text-[#111c2d]">
          {t('providerMyProposals') || 'My Proposals'}
        </h1>
        <p className="text-sm text-[#424654] mt-1">
          {t('providerMyProposalsNote') ||
            'Proposals you submitted for client quote requests.'}
        </p>
      </div>

      <div className="px-4 sm:px-6 py-6 flex flex-col gap-4">
        <div className="flex flex-wrap gap-2">
          {filters.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setStatusFilter(f.key)}
              className={`h-9 px-4 rounded-lg text-sm font-semibold border transition-colors ${
                statusFilter === f.key
                  ? 'bg-[#4285F4] text-white border-[#4285F4]'
                  : 'bg-white text-[#4285F4] border-[#d8e3fb] hover:bg-[#e7eeff]'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-4">
          {filteredProposals.map((proposal) => {
            const status = proposal?.status || 'submitted';
            const canEdit = status === 'submitted';
            const hourly =
              proposal?.paymentType === 'hourly' &&
              proposal?.hourlyRate != null &&
              proposal?.totalHours != null;
            const submittedDate = proposal.createdAt || proposal.updatedAt;
            const jobId =
              proposal?.requestQuoteJob?.uid ||
              proposal?.requestQuoteJobId ||
              '-';

            return (
              <div
                key={proposal._id}
                className="bg-white rounded-xl border border-[#d8e3fb] shadow-sm p-4 sm:p-5 flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6"
              >
                <div className="flex-1 min-w-0 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-6 gap-4 xl:gap-5">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[#424654]">
                      {t('jobId')}
                    </p>
                    <p
                      className="text-sm font-semibold text-[#111c2d] truncate mt-0.5"
                      title={String(jobId)}
                    >
                      {jobId}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[#424654]">
                      {t('service_name')}
                    </p>
                    <p
                      className="text-sm text-[#111c2d] truncate mt-0.5"
                      title={getServiceName(proposal, i18n.language)}
                    >
                      {getServiceName(proposal, i18n.language)}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[#424654]">
                      {t('client') || 'Client'}
                    </p>
                    <p
                      className="text-sm font-semibold text-[#111c2d] truncate mt-0.5"
                      title={getClientName(proposal)}
                    >
                      {getClientName(proposal)}
                    </p>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[#424654]">
                      {t('status')}
                    </p>
                    <span
                      className={`inline-flex w-fit max-w-full items-center rounded-full border px-3 py-1 text-xs font-semibold capitalize mt-1 ${statusBadgeClass(
                        status
                      )}`}
                    >
                      {t(`proposalStatus_${status}`) || status}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-[#424654]">
                      {t('submittedDate') || 'Submitted date'}
                    </p>
                    <p className="text-sm text-[#111c2d] mt-0.5 whitespace-nowrap">
                      {submittedDate
                        ? moment(submittedDate).format('DD MMM YYYY')
                        : '-'}
                    </p>
                  </div>
                  <div className="min-w-0 col-span-2 sm:col-span-1">
                    <p className="text-xs font-medium text-[#424654]">
                      {t('budget') || 'Amount'}
                    </p>
                    <p className="text-sm font-semibold text-[#111c2d] mt-0.5">
                      ${Number(proposal?.budget || 0).toFixed(2)}
                      {hourly ? (
                        <span className="block text-xs font-normal text-[#424654] truncate">
                          ({proposal.hourlyRate}/hr × {proposal.totalHours}h)
                        </span>
                      ) : null}
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => openProposalForm(proposal)}
                  className="h-10 shrink-0 w-full lg:w-auto px-5 rounded-lg bg-[#4285F4] text-white text-sm font-semibold shadow-sm hover:bg-[#4285F4] transition-colors whitespace-nowrap"
                >
                  {canEdit
                    ? t('edit_proposal') || 'Edit proposal'
                    : t('view_proposals') || 'View'}
                </button>
              </div>
            );
          })}

          {!fetching && filteredProposals.length === 0 && (
            <div className="bg-white rounded-xl border border-[#d8e3fb] px-6 py-12 text-center">
              <p className="text-sm text-[#424654]">
                {t('noProviderProposalsYet') ||
                  'You have not submitted any proposals yet.'}
              </p>
            </div>
          )}
        </div>

        {fetching && (
          <div className="flex flex-col gap-4">
            {[...Array(5)].map((_, i) => (
              <JobSkeleton key={i} />
            ))}
          </div>
        )}
      </div>

      <Popup
        isOpen={showFormPopup}
        handleClose={closeFormPopup}
        className="bg-white"
        size="md"
        headerTitle={t('edit_proposal') || 'Proposal'}
        isShowCloseIcon
        isShowBlackCrossIcon
        isShowDivider
        bodyChildren={
          activeRequestQuoteJobId ? (
            <div className="p-2 w-full overflow-y-auto max-h-[80vh]">
              <NewProposalForm
                requestQuoteJobId={activeRequestQuoteJobId}
                onSuccess={() => {
                  closeFormPopup();
                  fetchProposals();
                }}
                onClose={closeFormPopup}
              />
            </div>
          ) : null
        }
      />
    </ContentContainer>
  );
};

export default ProviderProposalsPage;
